import {
  APPEARANCE_FONTS,
  APPEARANCE_FONT_SIZES,
  APPEARANCE_MODES,
  APPEARANCE_SOURCES,
  APPEARANCE_THEMES,
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  type AppearanceFont,
  type AppearanceFontSize,
  type AppearanceMode,
  type AppearancePreferences,
  type AppearanceSource,
  type AppearanceTheme,
  type SavedManualAppearance,
} from "./types";

/** Legacy theme id from before Magic Hour rename. */
const LEGACY_THEME_ALIASES: Record<string, AppearanceTheme> = {
  "golden-hour": "magic-hour",
};
const ACTIVE_PREFERENCES_USER_KEY = "rivetai-preferences-active-user";
const USER_PREFERENCES_KEY_PREFIX = "rivetai-user-preferences";

function resolveAppearanceTheme(value: unknown): AppearanceTheme | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const aliased = LEGACY_THEME_ALIASES[value] ?? value;
  return APPEARANCE_THEMES.includes(aliased as AppearanceTheme)
    ? (aliased as AppearanceTheme)
    : undefined;
}

function isAppearanceMode(value: unknown): value is AppearanceMode {
  return typeof value === "string" && APPEARANCE_MODES.includes(value as AppearanceMode);
}

function isAppearanceSource(value: unknown): value is AppearanceSource {
  return typeof value === "string" && APPEARANCE_SOURCES.includes(value as AppearanceSource);
}

function isAppearanceFont(value: unknown): value is AppearanceFont {
  return typeof value === "string" && APPEARANCE_FONTS.includes(value as AppearanceFont);
}

function isAppearanceFontSize(value: unknown): value is AppearanceFontSize {
  return typeof value === "string" && APPEARANCE_FONT_SIZES.includes(value as AppearanceFontSize);
}

function normalizeSavedManual(value: unknown): SavedManualAppearance | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const raw = value as Partial<SavedManualAppearance>;
  const theme = resolveAppearanceTheme(raw.theme);
  if (!isAppearanceMode(raw.mode) || !theme) {
    return undefined;
  }

  return {
    mode: raw.mode,
    theme,
  };
}

export function normalizeAppearancePreferences(
  raw: Partial<AppearancePreferences>
): AppearancePreferences {
  const source = isAppearanceSource(raw.source) ? raw.source : DEFAULT_APPEARANCE.source;

  return {
    source,
    mode: isAppearanceMode(raw.mode) ? raw.mode : DEFAULT_APPEARANCE.mode,
    theme: resolveAppearanceTheme(raw.theme) ?? DEFAULT_APPEARANCE.theme,
    font: isAppearanceFont(raw.font) ? raw.font : DEFAULT_APPEARANCE.font,
    fontSize: isAppearanceFontSize(raw.fontSize) ? raw.fontSize : DEFAULT_APPEARANCE.fontSize,
    savedManual: normalizeSavedManual(raw.savedManual),
  };
}

export function isDefaultAppearance(preferences: AppearancePreferences): boolean {
  return (
    preferences.source === DEFAULT_APPEARANCE.source &&
    preferences.mode === DEFAULT_APPEARANCE.mode &&
    preferences.theme === DEFAULT_APPEARANCE.theme &&
    preferences.font === DEFAULT_APPEARANCE.font &&
    preferences.fontSize === DEFAULT_APPEARANCE.fontSize &&
    preferences.savedManual === undefined
  );
}

export function hasMeaningfulAppearance(
  appearance: Partial<AppearancePreferences> | undefined
): appearance is Partial<AppearancePreferences> {
  return Boolean(appearance && Object.keys(appearance).length > 0);
}

export function appearancePreferencesEqual(
  left: AppearancePreferences,
  right: AppearancePreferences
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function readLegacyAppearanceStorage(): AppearancePreferences | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<AppearancePreferences>;
    return normalizeAppearancePreferences(parsed);
  } catch {
    return null;
  }
}

export function readStoredAppearance(): AppearancePreferences {
  if (typeof window === "undefined") {
    return DEFAULT_APPEARANCE;
  }

  try {
    const activeUserId = window.localStorage.getItem(ACTIVE_PREFERENCES_USER_KEY);
    const userPreferencesRaw = activeUserId
      ? window.localStorage.getItem(`${USER_PREFERENCES_KEY_PREFIX}:${activeUserId}`)
      : null;
    const userPreferences = userPreferencesRaw
      ? (JSON.parse(userPreferencesRaw) as { appearance?: Partial<AppearancePreferences> })
      : null;
    if (userPreferences?.appearance) {
      return normalizeAppearancePreferences(userPreferences.appearance);
    }

    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_APPEARANCE;
    }

    const parsed = JSON.parse(raw) as Partial<AppearancePreferences>;
    return normalizeAppearancePreferences(parsed);
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

export function writeStoredAppearance(preferences: AppearancePreferences): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    /* ignore quota / private mode */
  }
}
